import React, { useEffect, useRef, useState } from 'react'
import { Autocomplete } from '@material-ui/lab'
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import clsx from 'clsx'
import useStyles from '../common/FormFields/FormFieldsStyle'
import { ReactComponent as AutocompleteIcon } from '../../images/autocomplete-icon.svg'
import api from '../../utils/api'

/**
 * A dynamic list of building+apartment pairs for assigning a user to
 * apartments across multiple buildings.
 *
 * Props:
 *   assignments    – [{ building, apartment }]
 *   onChange       – called with the new assignments array
 *   buildings      – all available buildings
 *   disableBuilding – lock the building field (e.g. when context forces one)
 */
export default function ApartmentAssignmentList({
  assignments,
  onChange,
  buildings,
  disableBuilding,
  disabled,
}) {
  const classes = useStyles()
  // buildingId → apartments[]
  const [apartmentsByBuilding, setApartmentsByBuilding] = useState({})
  const fetchedBuildings = useRef(new Set())

  const fetchApartments = async (building) => {
    if (!building || fetchedBuildings.current.has(building.id)) return
    fetchedBuildings.current.add(building.id)
    try {
      const response = await api.get(`/buildings/${building.id}/apartments`, {
        includeToken: true,
      })
      setApartmentsByBuilding((prev) => ({
        ...prev,
        [building.id]: response?.data?.data || [],
      }))
    } catch {
      setApartmentsByBuilding((prev) => ({ ...prev, [building.id]: [] }))
    }
  }

  // Fetch apartments for any building already selected on mount
  useEffect(() => {
    assignments.forEach((a) => {
      if (a.building) fetchApartments(a.building)
    })
  }, [])

  const updateRow = (index, partial) => {
    const next = assignments.map((a, i) => (i === index ? { ...a, ...partial } : a))
    onChange(next)
  }

  const addRow = () => {
    onChange([...assignments, { building: null, apartment: null }])
  }

  const removeRow = (index) => {
    onChange(assignments.filter((_, i) => i !== index))
  }

  return (
    <FormControl fullWidth style={{ marginBottom: '16px' }}>
      <div className={classes.inputLabel} style={{ marginBottom: '8px' }}>
        Unidades
      </div>

      {assignments.map((row, i) => (
        <div
          key={i}
          className={classes.flex}
          style={{ alignItems: 'flex-end', marginBottom: '16px', gap: '16px' }}
        >
          {/* Building selector */}
          <FormControl style={{ flex: 1 }}>
            <div className={classes.inputLabel}>Cliente</div>
            <Autocomplete
              options={buildings || []}
              getOptionLabel={(o) => o.name || ''}
              renderOption={(o) => o.name}
              noOptionsText={<span style={{ color: 'white' }}>Sin opciones</span>}
              classes={{ option: classes.selectOption, paper: classes.selectPaper }}
              value={row.building}
              disabled={disabled || disableBuilding}
              onChange={(_, value) => {
                updateRow(i, { building: value, apartment: null })
                if (value) fetchApartments(value)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Seleccione un cliente"
                  variant="outlined"
                  className={classes.customBorder}
                  InputProps={{
                    ...params.InputProps,
                    className: clsx(
                      params.InputProps.className,
                      !row.building && classes.inputFont,
                      classes.inputRoot
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton className={classes.iconButton}>
                          <AutocompleteIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              className={clsx(classes.fullFlexWidth, classes.centerText)}
            />
          </FormControl>

          {/* Apartment selector */}
          <FormControl style={{ flex: 1 }}>
            <div className={classes.inputLabel}>Unidad</div>
            <Autocomplete
              options={
                row.building ? apartmentsByBuilding[row.building.id] || [] : []
              }
              getOptionLabel={(o) => o.name || ''}
              renderOption={(o) => o.name}
              noOptionsText={
                <span style={{ color: 'white' }}>
                  {row.building
                    ? 'Sin opciones'
                    : 'Seleccione un cliente primero'}
                </span>
              }
              classes={{ option: classes.selectOption, paper: classes.selectPaper }}
              value={row.apartment}
              disabled={disabled || !row.building}
              onChange={(_, value) => updateRow(i, { apartment: value })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Seleccione una unidad"
                  variant="outlined"
                  className={classes.customBorder}
                  InputProps={{
                    ...params.InputProps,
                    className: clsx(
                      params.InputProps.className,
                      !row.apartment && classes.inputFont,
                      classes.inputRoot
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton className={classes.iconButton}>
                          <AutocompleteIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              className={clsx(classes.fullFlexWidth, classes.centerText)}
            />
          </FormControl>

          {/* Remove row — only visible when there are multiple rows and not disabled */}
          {!disabled && assignments.length > 1 && (
            <IconButton onClick={() => removeRow(i)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      ))}

      {!disabled && (
        <Button
          variant="outlined"
          className={classes.secondaryButton}
          startIcon={<AddIcon />}
          onClick={addRow}
          style={{ alignSelf: 'flex-start' }}
        >
          Agregar unidad
        </Button>
      )}
    </FormControl>
  )
}
